// ============================================================================
// Pagination Response Transformation Utilities
// ============================================================================
// Handles conversion between Spring Data pagination format and our expected format

import type { PaginatedResponse } from '@/types/common';

// Spring Data pagination format (what the API returns)
export interface SpringDataPaginatedResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  pageable?: unknown;
  last?: boolean;
  first?: boolean;
  empty?: boolean;
  numberOfElements?: number;
  sort?: unknown;
  [key: string]: unknown;
}

// Wrapper API response format
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  errorCode?: string;
  message?: string;
  timestamp?: string;
  [key: string]: unknown;
}

/**
 * Transform Spring Data pagination format to our expected format
 * Spring Data returns: { content: [], totalPages, totalElements, size, number, ... }
 * We expect: { content: [], page: { size, number, totalElements, totalPages } }
 */
export const transformSpringDataPagination = <T>(
  data: SpringDataPaginatedResponse<T> | PaginatedResponse<T> | unknown
): PaginatedResponse<T> => {
  try {
    if (!data) {
      console.warn('[transformSpringDataPagination] Invalid data:', data);
      return { content: [], page: { size: 0, number: 0, totalElements: 0, totalPages: 0 } };
    }

    // Handle plain arrays (API returns array directly)
    if (Array.isArray(data)) {
      return {
        content: data,
        page: {
          size: data.length,
          number: 0,
          totalElements: data.length,
          totalPages: 1,
        },
      };
    }

    if (typeof data !== 'object') {
      console.warn('[transformSpringDataPagination] Invalid data type:', typeof data);
      return { content: [], page: { size: 0, number: 0, totalElements: 0, totalPages: 0 } };
    }

    // Check if it's already in the expected format (has nested 'page' property)
    if (data && typeof data === 'object' && data !== null && 'page' in data && data.page && typeof data.page === 'object') {
      return data as PaginatedResponse<T>;
    }

    // Transform Spring Data format to expected format
    const springData = data as SpringDataPaginatedResponse<T>;
    
    return {
      content: Array.isArray(springData.content) ? springData.content : [],
      page: {
        size: springData.size ?? 0,
        number: springData.number ?? 0,
        totalElements: springData.totalElements ?? 0,
        totalPages: springData.totalPages ?? 0,
      },
    };
  } catch (error) {
    console.error('[transformSpringDataPagination] Error during transformation:', error, 'data:', data);
    throw error;
  }
};

/**
 * Unwrap paginated response from ApiResponse wrapper if needed, then transform
 * Handles both wrapped (ApiResponse) and direct Spring Data format responses
 */
export const unwrapPaginatedResponse = <T>(
  response: ApiResponse<PaginatedResponse<T>> | ApiResponse<unknown> | PaginatedResponse<T> | SpringDataPaginatedResponse<T> | { data?: unknown } | unknown
): PaginatedResponse<T> => {
  try {
    // Check if response is wrapped in ApiResponse (success + data)
    if (response && typeof response === 'object' && 'success' in response) {
      const wrapped = response as ApiResponse<unknown>;
      if (wrapped.success === false) {
        throw new Error(wrapped.message || 'API request failed');
      }
      if (wrapped.data === undefined || wrapped.data === null) {
        return { content: [], page: { size: 0, number: 0, totalElements: 0, totalPages: 0 } };
      }
      return transformSpringDataPagination<T>(wrapped.data);
    }

    // Plain { data: ... } wrapper (no success field)
    if (response && typeof response === 'object' && 'data' in response) {
      const wrapped = response as { data?: unknown };
      if (wrapped.data !== undefined && wrapped.data !== null) {
        return transformSpringDataPagination<T>(wrapped.data);
      }
    }

    return transformSpringDataPagination<T>(response);
  } catch (error) {
    console.error('[unwrapPaginatedResponse] Error during transformation:', error);
    throw error;
  }
};

/**
 * Helper to wrap ApiService.get calls for paginated endpoints
 * Automatically transforms Spring Data pagination format to our expected format
 */
export async function getPaginated<T>(
  apiService: { get: <R>(url: string) => Promise<R> },
  endpoint: string
): Promise<PaginatedResponse<T>> {
  const response = await apiService.get<unknown>(endpoint);
  return unwrapPaginatedResponse<T>(response);
}

